/**
 * /admin/ambassador-monitor?id=<uuid>&range=<7d|30d|90d|all>
 * لوحة مراقبة سفير بزاوية الأدمن (read + decision actions).
 * مكمّلة لـ/admin/preview-as-ambassador (انتحال شخصيّة) — هذه أرقام + قرارات.
 */
import { useState } from "react";
import { matchesAmbassadorSearch } from "@/lib/ambassador-search";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAdminGate } from "@/hooks/useAdminGate";
import { useLocale } from "@/contexts/LocaleContext";
import { useAmbassadorMonitor, useAmbassadorNotes, useAmbassadorMonitorActions, type MonitorRange } from "@/hooks/useAmbassadorMonitor";
import { adminListAmbassadorsForPreview } from "@/utils/ambassador.functions";
import { toast } from "@/lib/toast";
import { Eye, ShieldAlert, Trash2 } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { useAdminTexts } from "@/lib/useAdminTexts";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const tr = (locale: string, ar: string, en: string) => (locale === "ar" ? ar : en);

function useAdminAmbassadors() {
  const fetchList = useServerFn(adminListAmbassadorsForPreview);
  return useQuery({
    queryKey: ["admin-ambassadors-monitor-list"],
    queryFn: async () => {
      const r = await fetchList();
      return r.ambassadors;
    },
  });
}

export const Route = createFileRoute("/admin/ambassador-monitor")({
  validateSearch: (s: Record<string, unknown>) => ({
    id: typeof s.id === "string" ? s.id : undefined,
    range: (typeof s.range === "string" && ["7d", "30d", "90d", "all"].includes(s.range) ? s.range : "30d") as MonitorRange,
  }),
  component: AmbassadorMonitorPage,
});

const CHANNEL_COLORS: Record<string, string> = {
  instagram: "hsl(var(--primary))",
  tiktok: "hsl(var(--foreground))",
  whatsapp: "hsl(var(--accent))",
  facebook: "hsl(var(--primary) / 0.7)",
  snapchat: "hsl(var(--muted-foreground))",
  twitter: "hsl(var(--primary) / 0.5)",
  google: "hsl(var(--primary) / 0.85)",
  direct: "hsl(var(--muted-foreground) / 0.6)",
  other: "hsl(var(--muted-foreground) / 0.4)",
};

function AmbassadorMonitorPage() {
  useAdminTexts();
  const { id, range } = Route.useSearch();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { ready, isAdmin, loading } = useAdminGate();

  const setSearch = (next: Partial<{ id: string; range: MonitorRange }>) =>
    navigate({ to: "/admin/ambassador-monitor", search: { id, range, ...next } });

  if (loading) return <SiteShell><div className="mx-auto w-full max-w-6xl px-4 py-10 text-center text-sm text-muted-foreground sm:px-6">…</div></SiteShell>;
  if (!ready || !isAdmin) {
    return (
      <SiteShell>
        <div className="mx-auto w-full max-w-6xl px-4 py-16 text-center sm:px-6">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
          <p className="mt-4 text-sm text-muted-foreground">{tr(locale, "للأدمن فقط", "Admins only")}</p>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 space-y-6 sm:px-6">
        <PickerBar id={id} range={range} onChange={setSearch} />
        {id ? <MonitorBody id={id} range={range} /> : (
          <div className="rounded-none border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            {tr(locale, "اختر سفيراً من القائمة أعلاه", "Pick an ambassador from the list above")}
          </div>
        )}
      </div>
    </SiteShell>
  );
}


function PickerBar({ id, range, onChange }: { id?: string; range: MonitorRange; onChange: (n: Partial<{ id: string; range: MonitorRange }>) => void }) {
  const { locale } = useLocale();
  const { data: list } = useAdminAmbassadors();
  const [query, setQuery] = useState("");
  const options = (list ?? []).filter((a) => matchesAmbassadorSearch(a, query));
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-none border border-border bg-card p-3">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={tr(locale, "ابحث بالكود أو الاسم…", "Search by code or name…")}
        aria-label={tr(locale, "بحث عن سفير", "Search ambassadors")}
        className="min-w-[180px] rounded border border-border bg-background px-3 py-2 text-sm"
      />
      <select
        value={id ?? ""}
        onChange={(e) => onChange({ id: e.target.value })}
        className="min-w-[220px] rounded border border-border bg-background px-3 py-2 text-sm"
      >
        <option value="">{tr(locale, "— اختر سفيراً —", "— Pick ambassador —")}</option>
        {options.map((a) => (
          <option key={a.id} value={a.id}>{a.code} · {a.display_name ?? "—"}</option>
        ))}
      </select>


      <div className="flex gap-1 rounded border border-border p-1">
        {(["7d", "30d", "90d", "all"] as const).map((r) => (
          <button
            key={r}
            onClick={() => onChange({ range: r })}
            className={`rounded px-3 py-1.5 text-xs ${range === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            {r === "all" ? tr(locale, "الكل", "All") : r === "7d" ? tr(locale, "٧ أيّام", "7d") : r === "30d" ? tr(locale, "٣٠ يوم", "30d") : tr(locale, "٩٠ يوم", "90d")}
          </button>
        ))}
      </div>

      {id && (
        <Link
          to="/admin/preview-as-ambassador"
          search={{ id }}
          className="ms-auto inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-xs hover:border-primary hover:text-primary"
        >
          <Eye className="h-3.5 w-3.5" /> {tr(locale, "شاهد كما يراها هو", "Preview as ambassador")}
        </Link>
      )}
    </div>
  );
}

function MonitorBody({ id, range }: { id: string; range: MonitorRange }) {
  const { locale } = useLocale();
  const { data, isLoading, error } = useAmbassadorMonitor(id, range);

  if (isLoading) return <div className="py-10 text-center text-sm text-muted-foreground">…</div>;
  if (error) return <div className="rounded border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{String((error as Error).message)}</div>;
  if (!data) return null;

  const { ambassador, kpis, balances, timeseries, channels, lastOrders, lastClicks, alerts, topVisitors, repeatVisitorsCount } = data;
  const noActivity = kpis.totalClicksLifetime === 0 && kpis.orderCount === 0;
  const hasAdminBonus = kpis.adminAdjustmentsCount > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-none border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold">{ambassador.display_name ?? ambassador.code}</h1>
          <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono">{ambassador.code}</span>
          <TierBadge tier={ambassador.tier} />
          <StatusBadge status={ambassador.status} />
          {ambassador.relationship_type && <RelBadge rel={ambassador.relationship_type} />}
        </div>
        <div className="mt-1.5 text-xs text-muted-foreground">
          {ambassador.phone_e164 ?? "—"} · {ambassador.preferred_market ?? ambassador.country_code ?? "—"}
        </div>
      </div>

      {/* Empty-state explainer */}
      {noActivity && (
        <div className="rounded-none border border-dashed border-border bg-muted/30 p-4 text-sm">
          <div className="font-medium">{tr(locale, "لا نشاط فعليّ بعد", "No real activity yet")}</div>
          <p className="mt-1 text-xs text-muted-foreground">
            {tr(
              locale,
              kpis.sharesCountLifetime > 0
                ? `سجّل السفير ${kpis.sharesCountLifetime} مشاركة/نسخ رابط، لكن لم تصل نقرات أو طلبات بعد.`
                : "لا توجد مشاركات مسجَّلة ولا نقرات ولا طلبات في كلّ التاريخ.",
              kpis.sharesCountLifetime > 0
                ? `${kpis.sharesCountLifetime} link share/copy event(s) recorded, but no clicks or orders yet.`
                : "No recorded shares, clicks or orders across all time."
            )}
            {hasAdminBonus && (
              <> {tr(
                locale,
                "الرصيد الظاهر في المحفظة مصدره مكافأة منحتَها أنت يدويّاً من الأسفل، لا مبيعات.",
                "The wallet balance shown comes from a manual admin bonus you granted below — not from sales."
              )}</>
            )}
          </p>
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div
              key={i}
              className={`rounded border p-3 text-sm ${
                a.severity === "danger" ? "border-destructive/40 bg-destructive/10 text-destructive"
                : a.severity === "warn" ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                : "border-border bg-muted/30"
              }`}
            >
              {locale === "ar" ? a.message_ar : a.message_en}
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi
          label={tr(locale, "إجماليّ المبيعات", "Total Sales")}
          value={kpis.totalSales.toLocaleString(locale === "ar" ? "ar" : "en")}
          delta={kpis.totalSalesDelta}
        />
        <Kpi
          label={tr(locale, "النقرات", "Clicks")}
          value={kpis.rangeClickCount.toLocaleString()}
          delta={kpis.clicksDelta}
          sub={`${kpis.totalClicksLifetime.toLocaleString()} ${tr(locale, "إجمالي", "lifetime")}`}
        />
        <Kpi
          label={tr(locale, "معدّل التحويل", "Conversion")}
          value={`${kpis.conversionRate.toFixed(2)}%`}
          sub={`${kpis.orderCount} ${tr(locale, "طلب", "orders")}`}
        />
        <Kpi
          label={tr(locale, "الترتيب", "Rank")}
          value={kpis.rank > 0 ? `#${kpis.rank}` : "—"}
          sub={`/ ${kpis.totalAmbCount}`}
        />
        <Kpi
          label={tr(locale, "مكافآت الأدمن", "Admin Bonuses")}
          value={
            Object.keys(kpis.adminAdjustmentsByCurrency ?? {}).length === 0
              ? "—"
              : Object.entries(kpis.adminAdjustmentsByCurrency)
                  .map(([c, v]) => `${(v as number).toFixed(0)} ${c}`)
                  .join(" · ")
          }
          sub={
            kpis.adminAdjustmentsCount > 0
              ? `${kpis.adminAdjustmentsCount} ${tr(locale, "مكافأة", "bonus")}${(kpis as { lastAdminAdjustmentNote?: string | null }).lastAdminAdjustmentNote ? ` · ${(kpis as { lastAdminAdjustmentNote?: string | null }).lastAdminAdjustmentNote}` : ""}`
              : tr(locale, "لا مكافآت", "none")
          }
        />
      </div>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-none border border-border bg-card p-4 lg:col-span-2">
          <h3 className="mb-2 text-sm font-medium">{tr(locale, "نشاط يوميّ", "Daily activity")}</h3>
          {timeseries.length === 0 ? (
            <p className="py-16 text-center text-xs text-muted-foreground">
              {tr(locale, "لا مبيعات ولا نقرات في هذه الفترة", "No sales or clicks in this period")}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={timeseries}>
                <XAxis dataKey="date" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Line type="monotone" dataKey="clicks" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="orders" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="revenue" stroke="hsl(var(--muted-foreground))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-none border border-border bg-card p-4">
          <h3 className="mb-2 text-sm font-medium">{tr(locale, "توزيع القنوات", "Channels")}</h3>
          {channels.length === 0 ? (
            <p className="py-8 text-center text-xs text-muted-foreground">—</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={channels} dataKey="value" nameKey="name" outerRadius={80}>
                  {channels.map((c) => <Cell key={c.name} fill={CHANNEL_COLORS[c.name] ?? "hsl(var(--muted-foreground))"} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Balances */}
      <div className="rounded-none border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium">{tr(locale, "أرصدة المحفظة", "Wallet balances")}</h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Object.entries(balances).map(([cur, b]) => (
            <div key={cur} className="rounded border border-border p-3">
              <div className="text-xs font-mono text-muted-foreground">{cur}</div>
              <div className="mt-1 text-xs">
                <div>{tr(locale, "متاح", "Available")}: <span className="font-medium text-foreground">{b.available.toFixed(2)}</span></div>
                <div className="text-muted-foreground">{tr(locale, "معلَّق", "Pending")}: {b.pending.toFixed(2)}</div>
                <div className="text-muted-foreground">{tr(locale, "مدفوع", "Paid")}: {b.paid.toFixed(2)}</div>
              </div>
            </div>
          ))}
          {Object.keys(balances).length === 0 && <p className="text-xs text-muted-foreground">—</p>}
        </div>
      </div>

      {/* Tables */}
      <div className="grid gap-4 lg:grid-cols-2">
        <DataTable
          title={tr(locale, "آخر الطلبات", "Recent orders")}
          rows={lastOrders.map((o) => ({
            a: o.order_id ? `#${o.order_id.slice(-8)}` : "—",
            b: `${o.amount.toFixed(2)} ${o.currency}`,
            c: o.created_at ? new Date(o.created_at).toLocaleDateString(locale === "ar" ? "ar" : "en") : "—",
          }))}
          empty={tr(locale, "لا طلبات في هذه الفترة", "No orders in this period")}
        />
        <DataTable
          title={tr(locale, "آخر النقرات", "Recent clicks")}
          rows={lastClicks.map((c) => ({
            a: c.country ?? "—",
            b: [c.device, c.source].filter(Boolean).join(" · ") || "—",
            c: c.at ? new Date(c.at).toLocaleString(locale === "ar" ? "ar" : "en") : "—",
          }))}
          empty={tr(locale, "لا نقرات في هذه الفترة", "No clicks in this period")}
        />
      </div>

      {/* Repeat visitors (بصمة مشفّرة — لا IP خام) */}
      <div className="rounded-none border border-border bg-card p-4">
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-medium">{tr(locale, "الزوّار (بصمة الجهاز)", "Visitors (device fingerprint)")}</h3>
          <span className="text-xs text-muted-foreground">
            {tr(locale, "زائر مكرّر:", "Repeat visitors:")} <span className="font-medium text-foreground">{repeatVisitorsCount}</span>
          </span>
        </div>
        <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
          {tr(
            locale,
            "كلّ صفّ جهاز/شبكة مختلفة. نفس المُعرِّف في أكثر من يوم = نفس الزائر عاد. لا نخزّن عنوان IP نهائيّاً — فقط بصمة مشفّرة لا يمكن عكسها.",
            "Each row is a distinct device/network. Same ID on different days = same visitor returned. We never store raw IPs — only an irreversible fingerprint."
          )}
        </p>
        {topVisitors.length === 0 ? (
          <p className="text-xs text-muted-foreground">{tr(locale, "لا بصمات بعد — تُسجَّل من النقرات الجديدة فصاعداً.", "No fingerprints yet — recorded from new clicks onward.")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-start text-muted-foreground">
                  <th className="py-1.5 pe-2 text-start font-medium">{tr(locale, "المُعرِّف", "ID")}</th>
                  <th className="py-1.5 pe-2 text-start font-medium">{tr(locale, "نقرات", "Clicks")}</th>
                  <th className="py-1.5 pe-2 text-start font-medium">{tr(locale, "الدولة", "Country")}</th>
                  <th className="py-1.5 pe-2 text-start font-medium">{tr(locale, "الجهاز", "Device")}</th>
                  <th className="py-1.5 pe-2 text-start font-medium">{tr(locale, "أوّل ظهور", "First seen")}</th>
                  <th className="py-1.5 text-start font-medium">{tr(locale, "آخر ظهور", "Last seen")}</th>
                </tr>
              </thead>
              <tbody>
                {topVisitors.map((v) => (
                  <tr key={v.hash} className="border-b border-border/50 last:border-0">
                    <td className="py-1.5 pe-2 font-mono">{v.hash}</td>
                    <td className="py-1.5 pe-2 font-medium">{v.clicks}{v.clicks > 1 && <span className="ms-1 text-amber-600">↺</span>}</td>
                    <td className="py-1.5 pe-2">{v.country ?? "—"}</td>
                    <td className="py-1.5 pe-2">{v.device ?? "—"}</td>
                    <td className="py-1.5 pe-2">{new Date(v.firstSeen).toLocaleDateString(locale === "ar" ? "ar" : "en")}</td>
                    <td className="py-1.5">{new Date(v.lastSeen).toLocaleDateString(locale === "ar" ? "ar" : "en")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin actions */}
      <AdminActionsCard id={id} status={ambassador.status} tier={ambassador.tier} />

      {/* Notes */}
      <NotesCard id={id} />
    </div>
  );
}

// ── Small UI bits ───────────────────────────────────────────────────────

function Kpi({ label, value, delta, sub }: { label: string; value: string; delta?: number | null; sub?: string }) {
  const dColor = delta == null ? "text-muted-foreground" : delta >= 0 ? "text-emerald-600" : "text-destructive";
  const arrow = delta == null ? "" : delta >= 0 ? "↑" : "↓";
  return (
    <div className="rounded-none border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
      <div className={`mt-1 text-xs ${dColor}`}>
        {delta != null && <>{arrow} {Math.abs(delta).toFixed(1)}% </>}
        {sub && <span className="text-muted-foreground">{sub}</span>}
      </div>
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const { locale } = useLocale();
  const map: Record<string, { ar: string; en: string; cls: string }> = {
    decouverte: { ar: "Lecmo Lover", en: "Lecmo Lover", cls: "bg-blue-500/10 text-blue-600" },
    active: { ar: "Lecmo Ambassador", en: "Lecmo Ambassador", cls: "bg-emerald-500/10 text-emerald-600" },
    elite: { ar: "Super Star", en: "Super Star", cls: "bg-amber-500/10 text-amber-600" },
  };
  const m = map[tier] ?? { ar: tier, en: tier, cls: "bg-muted" };
  return <span className={`rounded px-2 py-0.5 text-xs ${m.cls}`}>{tr(locale, m.ar, m.en)}</span>;
}

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    approved: "bg-emerald-500/10 text-emerald-600",
    pending: "bg-amber-500/10 text-amber-600",
    rejected: "bg-destructive/10 text-destructive",
    suspended: "bg-destructive/10 text-destructive",
  };
  return <span className={`rounded px-2 py-0.5 text-xs ${cls[status] ?? "bg-muted"}`}>{status}</span>;
}

function RelBadge({ rel }: { rel: string }) {
  return <span className="rounded bg-purple-500/10 px-2 py-0.5 text-xs text-purple-600">{rel}</span>;
}

function DataTable({ title, rows, empty }: { title: string; rows: Array<{ a: string; b: string; c: string }>; empty: string }) {
  return (
    <div className="rounded-none border border-border bg-card">
      <div className="border-b border-border p-3 text-sm font-medium">{title}</div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-muted-foreground">{empty}</div>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-3 gap-2 p-3 text-xs">
              <div className="font-medium">{r.a}</div>
              <div className="text-muted-foreground">{r.b}</div>
              <div className="text-end text-muted-foreground">{r.c}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Admin Actions ──────────────────────────────────────────────────────

function AdminActionsCard({ id, status, tier }: { id: string; status: string; tier: string }) {
  const { locale } = useLocale();
  const { promote, grantBonus, toggleStatus } = useAmbassadorMonitorActions(id);

  return (
    <div className="rounded-none border-2 border-primary/40 bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold text-primary">{tr(locale, "قرارات الأدمن", "Admin Actions")}</h3>
      <div className="grid gap-3 md:grid-cols-3">
        <PromoteBox tier={tier} onSubmit={(v) => promote.mutate(v, {
          onSuccess: () => toast.success(tr(locale, "تمّت الترقية", "Promoted")),
          onError: (e) => toast.error((e as Error).message),
        })} pending={promote.isPending} />

        <BonusBox onSubmit={(v) => grantBonus.mutate(v, {
          onSuccess: () => toast.success(tr(locale, "تمّ منح المكافأة", "Bonus granted")),
          onError: (e) => toast.error((e as Error).message),
        })} pending={grantBonus.isPending} />

        <StatusBox status={status} onSubmit={(v) => toggleStatus.mutate(v, {
          onSuccess: () => toast.success(tr(locale, "تمّ التحديث", "Updated")),
          onError: (e) => toast.error((e as Error).message),
        })} pending={toggleStatus.isPending} />
      </div>
    </div>
  );
}

function PromoteBox({ tier, onSubmit, pending }: { tier: string; onSubmit: (v: { tier: "decouverte" | "active" | "elite"; reason: string; lockTier: boolean }) => void; pending: boolean }) {
  const { locale } = useLocale();
  const [next, setNext] = useState<"decouverte" | "active" | "elite">(tier as never);
  const [reason, setReason] = useState("");
  const [lock, setLock] = useState(true);
  return (
    <div className="rounded border border-border p-3">
      <div className="mb-2 text-xs font-medium">{tr(locale, "ترقية يدويّة", "Manual Promotion")}</div>
      <select value={next} onChange={(e) => setNext(e.target.value as never)} className="mb-2 w-full rounded border border-border bg-background px-2 py-1 text-xs">
        <option value="decouverte">Lecmo Lover</option>
        <option value="active">Lecmo Ambassador</option>
        <option value="elite">Super Star</option>
      </select>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={tr(locale, "السبب…", "Reason…")} className="mb-2 w-full rounded border border-border bg-background px-2 py-1 text-xs" rows={2} />
      <label className="mb-2 flex items-center gap-1.5 text-xs"><input type="checkbox" checked={lock} onChange={(e) => setLock(e.target.checked)} />{tr(locale, "قفل الشريحة (لا تتغيّر آليّاً)", "Lock tier")}</label>
      <button
        disabled={pending || reason.length < 3}
        onClick={() => onSubmit({ tier: next, reason, lockTier: lock })}
        className="w-full rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
      >
        {pending ? "…" : tr(locale, "نفّذ", "Apply")}
      </button>
    </div>
  );
}

function BonusBox({ onSubmit, pending }: { onSubmit: (v: { amount: number; currency: "AED" | "SAR" | "USD" | "OMR" | "BRL"; reason: string }) => void; pending: boolean }) {
  const { locale } = useLocale();
  const [amount, setAmount] = useState("");
  const [cur, setCur] = useState<"AED" | "SAR" | "USD" | "OMR" | "BRL">("AED");
  const [reason, setReason] = useState("");
  const n = Number(amount);
  const valid = n > 0 && n <= 10000 && reason.length >= 3;
  return (
    <div className="rounded border border-border p-3">
      <div className="mb-2 text-xs font-medium">{tr(locale, "منح مكافأة", "Grant Bonus")}</div>
      <div className="mb-2 flex gap-2">
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="0" className="w-full rounded border border-border bg-background px-2 py-1 text-xs" />
        <select value={cur} onChange={(e) => setCur(e.target.value as never)} className="rounded border border-border bg-background px-2 py-1 text-xs">
          <option>AED</option><option>SAR</option><option>USD</option><option>OMR</option><option>BRL</option>
        </select>
      </div>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={tr(locale, "السبب…", "Reason…")} className="mb-2 w-full rounded border border-border bg-background px-2 py-1 text-xs" rows={2} />
      <button
        disabled={pending || !valid}
        onClick={() => onSubmit({ amount: n, currency: cur, reason })}
        className="w-full rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
      >
        {pending ? "…" : tr(locale, "أضف للمحفظة", "Add to wallet")}
      </button>
    </div>
  );
}

function StatusBox({ status, onSubmit, pending }: { status: string; onSubmit: (v: { nextStatus: "approved" | "suspended"; reason: string }) => void; pending: boolean }) {
  const { locale } = useLocale();
  const [reason, setReason] = useState("");
  const isSuspended = status === "suspended";
  const next: "approved" | "suspended" = isSuspended ? "approved" : "suspended";
  return (
    <div className="rounded border border-border p-3">
      <div className="mb-2 text-xs font-medium">{isSuspended ? tr(locale, "تنشيط الحساب", "Activate Account") : tr(locale, "تجميد الحساب", "Suspend Account")}</div>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={tr(locale, "السبب…", "Reason…")} className="mb-2 w-full rounded border border-border bg-background px-2 py-1 text-xs" rows={3} />
      <button
        disabled={pending || reason.length < 3}
        onClick={() => { if (confirm(tr(locale, "متأكّد؟", "Are you sure?"))) onSubmit({ nextStatus: next, reason }); }}
        className={`w-full rounded px-3 py-1.5 text-xs disabled:opacity-50 ${isSuspended ? "bg-primary text-primary-foreground" : "bg-destructive text-destructive-foreground"}`}
      >
        {pending ? "…" : isSuspended ? tr(locale, "نشّط", "Activate") : tr(locale, "جمّد", "Suspend")}
      </button>
    </div>
  );
}

function NotesCard({ id }: { id: string }) {
  const { locale } = useLocale();
  const { data: notes } = useAmbassadorNotes(id);
  const { addNote, deleteNote } = useAmbassadorMonitorActions(id);
  const [draft, setDraft] = useState("");

  return (
    <div className="rounded-none border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold">{tr(locale, "ملاحظات داخليّة (لا يراها السفير)", "Internal Notes (private)")}</h3>
      <div className="mb-3 flex gap-2">
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={tr(locale, "اكتب ملاحظة…", "Write a note…")} className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-sm" rows={2} />
        <button
          disabled={addNote.isPending || draft.trim().length === 0}
          onClick={() => addNote.mutate(draft.trim(), { onSuccess: () => { setDraft(""); toast.success(tr(locale, "أُضيفت", "Added")); } })}
          className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
        >
          {tr(locale, "أضف", "Add")}
        </button>
      </div>
      <div className="space-y-2">
        {(notes ?? []).map((n) => (
          <div key={n.id} className="rounded border border-border p-2.5 text-sm">
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>{n.author_name} · {new Date(n.created_at).toLocaleString(locale === "ar" ? "ar" : "en")}</span>
              <button onClick={() => { if (confirm(tr(locale, "حذف؟", "Delete?"))) deleteNote.mutate(n.id); }} className="hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="whitespace-pre-wrap">{n.note}</div>
          </div>
        ))}
        {(notes ?? []).length === 0 && <p className="text-xs text-muted-foreground">{tr(locale, "لا ملاحظات", "No notes")}</p>}
      </div>
    </div>
  );
}
